import React, { useState, useEffect } from "react";
import { fetchUserProfileImage, formatTime } from "../../../utils/utils";
import NotExistMsg from "../Comment/NotExistMsg";
import LoadingError from "../Comment/LoadingError";
import { AvatarGenerator } from 'random-avatar-generator';
import { Link } from "react-router-dom";
import "../NFTDetailDesign.css";
import "./ReactionsModal.css";
let generator = new AvatarGenerator();

const REACTION_TABS = [
    { key: "like", label: "Like", emoji: "👍" },
    { key: "love", label: "Love", emoji: "❤️" },
    { key: "fire", label: "Fire", emoji: "🔥" },
    { key: "party", label: "Party", emoji: "🎉" },
    { key: "dislike", label: "Dislike", emoji: "👎" }
];

const ReactionsModal = ({ isOpen, onClose, reactionsByType, count, totalReactions, fetchReactionLoader, fetchReactionError, loadMoreListed, reactionListPagination, currentUser }) => {
    const [activeTab, setActiveTab] = useState(REACTION_TABS[0].key);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 300);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    if (!isOpen && !isClosing) return null;

    const filteredReactions = reactionsByType.filter(c => c.reaction_type === activeTab);
   

    return (
        <div
            className={`reactions-modal-overlay ${isClosing ? 'closing' : ''}`}
            onClick={handleOverlayClick}
        >
            <div className="reactions-modal">
                {/* Header */}
                <div className="reactions-modal-header d-flex align-items-center justify-content-between">
                    <h6 className="reactions-modal-title">
                        Reactions {totalReactions > 0 && `(${totalReactions})`}
                    </h6>
                    <button
                        type="button"
                        className="reactions-modal-close"
                        aria-label="Close"
                        onClick={handleClose}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Tabs */}
                <div className="reactions-tabs">
                    {REACTION_TABS.map((tab) => {
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                className={`reaction-tab-button ${isActive ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                                aria-label={`${tab.label} reactions`}
                            >
                                <span className="reaction-tab-emoji">{tab.emoji}</span>
                                <span className="reaction-tab-badge">{count[tab.key] || 0}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="reactions-modal-content">
                    {fetchReactionError && !fetchReactionLoader ? (
                        <div className="reactions-error-state">
                            <div className="reactions-error-icon">
                                <i className="fas fa-exclamation-circle"></i>
                            </div>
                            <div className="reactions-error-title">Unable to load reactions</div>
                            <div className="reactions-error-message">
                                Something went wrong while loading reactions. Please check your connection and try again.
                            </div>
                        </div>
                    ) : fetchReactionLoader && reactionsByType.length === 0 ? (
                        <div className="reactions-loading">
                            <div className="reactions-spinner"></div>
                        </div>
                    ) : !fetchReactionLoader && filteredReactions.length === 0 ? (
                        <div className="reactions-empty-state">
                            <div className="reactions-empty-icon">
                                {REACTION_TABS.find(t => t.key === activeTab)?.emoji || "😊"}
                            </div>
                            <div className="reactions-empty-title">No {activeTab} reactions yet</div>
                            <div className="reactions-empty-message">Be the first to react!</div>
                        </div>
                    ) : (
                        <>
                            <div className="reactions-list">
                                {filteredReactions.map((reaction, idx) => {
                                    const reactionTab = REACTION_TABS.find(t => t.key === reaction.reaction_type);
                                    const userMeta = reaction?.user_meta || {};
                                   
                                    const userName = userMeta?.name || "Unknown User";
                                    const userProfileImage = userMeta?.profile_image;
                                    const userAvatar = userProfileImage
                                        ? fetchUserProfileImage(userProfileImage)
                                        : generator.generateRandomAvatar(userMeta?.user_name || userName);

                                    return (
                                        <div key={reaction._id || idx} className="reaction-item">
                                            <div className="reaction-user-info">
                                                <div className="reaction-avatar-container">
                                                    <img
                                                        src={userAvatar}
                                                        alt={userName}
                                                        className="reaction-avatar"
                                                    />
                                                </div>
                                                <div className="reaction-user-details">
                                                    <Link to={`/profile/${userMeta?.user_name}`}>
                                                    <div className="reaction-user-name">
                                                        {userName}
                                                        {userMeta?.user_name === currentUser?.user_name && (
                                                            <span style={{ marginLeft: 6, fontSize: 12, color: '#667eea' }}>(You)</span>
                                                        )}
                                                    </div>
                                                    </Link>
                                                    {reaction.updatedAt && (
                                                        <div className="reaction-timestamp">
                                                            {formatTime(reaction.updatedAt)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="reaction-emoji-display">
                                                <span>
                                                    {reactionTab?.emoji || ""}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {reactionListPagination?.has_next_page && (
                                <div className="reactions-load-more-container">
                                    {fetchReactionLoader ? (
                                        <div className="reactions-spinner"></div>
                                    ) : (
                                        <button
                                            onClick={loadMoreListed}
                                            className="reactions-load-more-btn"
                                            disabled={fetchReactionLoader}
                                        >
                                            <i className="fas fa-arrow-down"></i>
                                            <span>Load More</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReactionsModal;
